import { useEffect, useState, memo } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Sparkles, Zap } from "lucide-react";
import api, { money, fmtDate } from "../lib/api";

const CATS = ["Todos", "Shows"];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [cat, setCat] = useState("Todos");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/events", { params: cat !== "Todos" ? { category: cat } : {} })
      .then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, [cat]);

  return (
    <div>
      <section className="relative py-10 md:py-20 fade-in">
        <div className="absolute inset-0 -z-10 opacity-70"
             style={{ background: "radial-gradient(600px 300px at 20% 10%, rgba(0,230,118,0.20), transparent 60%), radial-gradient(500px 300px at 80% 0%, rgba(0,216,246,0.18), transparent 60%)" }} />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-[#00E676] mb-4">
            <Sparkles size={14}/> Conectando você ao seu próximo evento
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-none">
            A batida do <span className="text-[#00E676]">Brasil</span> começa no
            <span className="block text-[#00D8F6]">seu próximo ingresso.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-base md:text-lg max-w-xl">
            Compre em segundos com PIX ou cartão, receba o QR Code por e-mail e passe direto pela portaria — sem filas, sem fricção.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a href="#eventos" className="btn-primary px-6 py-3 rounded-full inline-flex items-center gap-2"
               data-testid="hero-cta">
              <Zap size={16}/> Ver eventos
            </a>
            <Link to="/meus-ingressos" data-testid="hero-lookup"
                  className="px-6 py-3 rounded-full border border-white/15 hover:border-white/40 transition">
              Meus ingressos
            </Link>
          </div>
        </div>
      </section>

      <section id="eventos" className="mt-4">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h2 className="font-display text-2xl md:text-3xl font-bold">Próximos eventos</h2>
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c.toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${
                  cat === c ? "bg-[#00E676] text-black border-[#00E676]"
                  : "border-white/15 text-slate-300 hover:border-white/40"
                }`}>{c}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="card-surface rounded-2xl h-72 animate-pulse"/>)}
          </div>
        ) : events.length === 0 ? (
          <div className="card-surface rounded-2xl p-10 text-center">
            <p className="text-slate-400">Nenhum evento nesta categoria.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {events.map((e) => <EventCard key={e.id} e={e}/>)}
          </div>
        )}
      </section>
    </div>
  );
}

const EventCard = memo(function EventCard({ e }) {
  const min = Math.min(...e.ticket_types.map(t => t.price));
  const prefetch = () => import(/* webpackPrefetch: true */ "./EventDetail");
  return (
    <Link to={`/eventos/${e.id}`} data-testid={`event-card-${e.id}`}
          onMouseEnter={prefetch} onFocus={prefetch}
          className="group card-surface rounded-2xl overflow-hidden hover:-translate-y-1 hover:glow-green transition-transform duration-300">
      <div className="aspect-square overflow-hidden">
        <img src={e.banner_url} alt={e.title} loading="lazy" decoding="async"
             className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-xs text-[#00D8F6] font-mono uppercase tracking-wider">
          <Calendar size={12}/> {fmtDate(e.date)}
        </div>
        <h3 className="mt-2 font-display font-bold text-lg leading-tight line-clamp-2">{e.title}</h3>
        <div className="mt-2 flex items-center gap-1 text-sm text-slate-400">
          <MapPin size={14}/> {e.venue} · {e.city}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">A partir de</div>
            <div className="font-display font-bold text-xl text-[#00E676]">{money(min)}</div>
          </div>
          <span className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10">{e.category}</span>
        </div>
      </div>
    </Link>
  );
});
